import { compareLine, glue, sourceLabel } from "@/helpers/resultsCopy";

export const SOURCES = ["network", "gitcha"];

const num = (value) => Number(value) || 0;

export const sumChanges = (changes) => {
  const present = changes.filter(Boolean);
  if (!present.length) return null;
  return {
    current: present.reduce((sum, change) => sum + num(change.current), 0),
    previous: present.reduce((sum, change) => sum + num(change.previous), 0),
  };
};

const addArrays = (arrays) => {
  const present = arrays.filter(Array.isArray);
  if (!present.length) return null;
  const length = present[0].length;
  if (present.some((values) => values.length !== length)) return null;
  return Array.from({ length }, (_, index) => present.reduce((sum, values) => sum + num(values[index]), 0));
};

const TILE_SPECS = (networkName) => [
  {
    key: "impressions",
    label: "Impressions",
    unit: ["impression", "impressions"],
    parts: [
      { source: "network", change: (data) => data.networkImpressions, series: "networkImpressions" },
      { source: "gitcha", change: (data) => data.gitchaImpressions, series: "gitchaImpressions" },
    ],
  },
  {
    key: "views",
    label: "Views",
    unit: ["view", "views"],
    parts: [
      { source: "network", change: (data) => data.buySide?.agentViews, series: "agentViews" },
      { source: "gitcha", change: (data) => data.listingViews, series: "listingViews" },
    ],
  },
  {
    key: "bioOpens",
    label: "Bio opens",
    unit: ["open", "opens"],
    parts: [{ source: "gitcha", change: (data) => data.bioOpens, series: "bioOpens" }],
  },
  {
    key: "introductions",
    label: "Introductions",
    unit: ["introduction", "introductions"],
    parts: [{ source: "network", change: (data) => data.buySide?.introsReceived, series: "introsReceived" }],
  },
  {
    key: "contacts",
    label: "Contacts",
    unit: ["person", "people"],
    parts: [{ source: "gitcha", change: (data) => data.contacts, series: "contacts" }],
  },
];

const liveParts = (parts, bySource) =>
  parts.filter((part) => bySource[part.source] && part.change(bySource[part.source]));

export const buildTiles = (bySource, networkName = "LAR") =>
  TILE_SPECS(networkName)
    .map((spec) => {
      const parts = liveParts(spec.parts, bySource);
      if (!parts.length) return null;
      const change = sumChanges(parts.map((part) => part.change(bySource[part.source])));
      return { key: spec.key, label: spec.label, unit: spec.unit, value: change.current, change };
    })
    .filter(Boolean);

export const buildSeries = (bySource, networkName = "LAR") => {
  const withSeries = SOURCES.map((source) => bySource[source]?.series).filter((series) => series?.days?.length);
  if (!withSeries.length) return null;
  const days = withSeries[0].days;
  const current = {};
  const previous = {};
  TILE_SPECS(networkName).forEach((spec) => {
    const parts = liveParts(spec.parts, bySource).filter((part) => {
      const series = bySource[part.source].series;
      return series?.days?.length === days.length;
    });
    if (!parts.length) return;
    const now = addArrays(parts.map((part) => bySource[part.source].series.current?.[part.series]));
    if (now) current[spec.key] = now;
    const before = addArrays(parts.map((part) => bySource[part.source].series.previous?.[part.series]));
    if (before) previous[spec.key] = before;
  });
  const published = addArrays(
    SOURCES.map((source) => bySource[source]?.series)
      .filter((series) => series?.days?.length === days.length)
      .map((series) => series.published),
  );
  return { days, current, previous, ...(published ? { published } : {}) };
};

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

const HEADLINE_ORDER = (networkName, bySource) => {
  const both = bySource.network && bySource.gitcha;
  const listings = bySource.network && !bySource.gitcha ? "Your Buyer Want-Listings" : both ? "Your Buyer Want-Listings" : "Your public Buyer Want-Listings";
  const viewers = bySource.network && !bySource.gitcha ? ` from ${networkName} agents` : "";
  return [
    { key: "views", line: (n) => `${listings} got ${plural(n, "view", "views")}${viewers}` },
    { key: "impressions", line: (n) => `${listings} showed up ${plural(n, "time", "times")}` },
    { key: "introductions", line: (n) => `${networkName} agents sent you ${plural(n, "introduction", "introductions")}` },
    { key: "contacts", line: (n) => `${plural(n, "person", "people")} contacted you from Gitcha.com` },
    { key: "bioOpens", line: (n) => `${plural(n, "person", "people")} opened your Gitcha bio` },
  ];
};

export const analyticsHeadline = (tiles, bySource, days, networkName = "LAR") => {
  const byKey = Object.fromEntries(tiles.map((tile) => [tile.key, tile]));
  const lead = HEADLINE_ORDER(networkName, bySource).find((option) => num(byKey[option.key]?.value) > 0);
  if (!lead) {
    return {
      title: glue("Your Buyer Want-Listings are ready to be seen"),
      sub: `Nothing in the last ${days} days yet. Buyer Want-Listings with a price range, areas and must-haves get the most views.`,
      empty: true,
    };
  }
  const tile = byKey[lead.key];
  return {
    title: glue(`${lead.line(num(tile.value))} in the last ${days} days`),
    sub: compareLine(tile.change, days),
    empty: false,
  };
};

export const mergeRealtime = (bySource) => {
  const blocks = SOURCES.map((source) => bySource[source]?.realtime).filter((block) => Array.isArray(block?.hours));
  if (!blocks.length) return null;
  const hours = addArrays(blocks.map((block) => block.hours)) || blocks[0].hours;
  return { hours, total: hours.reduce((sum, value) => sum + num(value), 0), start: blocks[0].start };
};

export const mergeTopListings = (bySource, limit = 5) => {
  const byId = new Map();
  SOURCES.forEach((source) => {
    (bySource[source]?.topListings || []).forEach((row) => {
      const found = byId.get(row.id);
      if (!found) {
        byId.set(row.id, { ...row, views: num(row.views), intros: num(row.intros), contacts: num(row.contacts) });
        return;
      }
      byId.set(row.id, {
        ...found,
        map: found.map || row.map,
        slug: found.slug || row.slug,
        views: found.views + num(row.views),
        intros: found.intros + num(row.intros),
        contacts: found.contacts + num(row.contacts),
      });
    });
  });
  return [...byId.values()]
    .sort((a, b) => b.views + b.intros + b.contacts - (a.views + a.intros + a.contacts) || b.views - a.views)
    .slice(0, limit);
};

export const mergeFeed = (bySource) =>
  SOURCES.flatMap((source) => (bySource[source]?.feed || []).map((item) => ({ ...item, feedKind: source }))).sort(
    (a, b) => String(b.at || "").localeCompare(String(a.at || "")),
  );

export const parseSources = (params, available = SOURCES) => {
  const legacy = params.get("tab");
  const raw = params.get("sources");
  let picked = raw ? raw.split(",") : legacy === "network" || legacy === "gitcha" ? [legacy] : available;
  picked = available.filter((source) => picked.includes(source));
  return picked.length ? picked : available;
};

export const toggleSource = (selected, source) => {
  if (!selected.includes(source)) return SOURCES.filter((key) => key === source || selected.includes(key));
  const next = selected.filter((key) => key !== source);
  return next.length ? next : selected;
};

const SOURCE_NAME = { network: "network", gitcha: "gitcha" };

const countsLine = (parts) =>
  parts
    .filter(([count]) => num(count) > 0)
    .map(([count, one, many]) => `${num(count)} ${num(count) === 1 ? one : many}`)
    .join(" · ");

export const foundYouRows = (bySource, networkName = "LAR") => {
  const rows = [];
  const network = bySource[SOURCE_NAME.network];
  if (network) {
    const views = num(network.buySide?.agentViews?.current);
    const intros = num(network.buySide?.introsReceived?.current);
    const messages = num(network.mix?.messagesFromAgents);
    const total = views + intros + messages;
    if (total > 0) {
      rows.push({
        key: "network",
        label: `${networkName} agents`,
        value: total,
        detail: countsLine([
          [views, "view", "views"],
          [intros, "introduction", "introductions"],
          [messages, "message", "messages"],
        ]),
      });
    }
  }
  const gitcha = bySource[SOURCE_NAME.gitcha];
  if (gitcha) {
    if (Array.isArray(gitcha.sources)) {
      gitcha.sources.forEach((row) => {
        const total = num(row.views) + num(row.contacts);
        if (total > 0) {
          rows.push({
            key: `gitcha-${row.source}`,
            label: sourceLabel(row.source),
            value: total,
            detail: countsLine([
              [row.views, "view", "views"],
              [row.contacts, "contact", "contacts"],
            ]),
          });
        }
      });
    } else {
      const views = num(gitcha.listingViews?.current);
      const contacts = num(gitcha.contacts?.current);
      if (views + contacts > 0) {
        rows.push({
          key: "gitcha",
          label: "Gitcha.com",
          value: views + contacts,
          detail: countsLine([
            [views, "view", "views"],
            [contacts, "contact", "contacts"],
          ]),
        });
      }
    }
  }
  return rows.sort((a, b) => b.value - a.value);
};

export const reachedYouRows = (bySource) => {
  const network = bySource[SOURCE_NAME.network];
  const gitcha = bySource[SOURCE_NAME.gitcha];
  const mix = gitcha?.mix || null;
  const rows = [];
  if (network) rows.push({ key: "intros", label: "Introductions", value: num(network.buySide?.introsReceived?.current) });
  if (network || mix) {
    rows.push({
      key: "messages",
      label: "Messages",
      value: num(network?.mix?.messagesFromAgents) + num(mix?.message) + num(mix?.email),
    });
  }
  if (mix) {
    rows.push(
      { key: "buyerForm", label: "Filled out your Buyer Listing Form", value: num(mix.buyer_form) },
      { key: "call", label: "Tapped Call", value: num(mix.call), note: "They saw your number. We can't see whether the call connected." },
      { key: "text", label: "Tapped Text", value: num(mix.text) },
      { key: "demandCheck", label: "Checked buyer demand on your website", value: num(mix.demand_check) },
    );
  } else if (gitcha) {
    rows.push({ key: "contacts", label: "Contacted you from Gitcha.com", value: num(gitcha.contacts?.current) });
  }
  return rows.filter((row) => row.value > 0).sort((a, b) => b.value - a.value);
};

export const listingsRows = (bySource) => {
  const network = bySource[SOURCE_NAME.network];
  if (!network?.sellSide) return null;
  return [
    { key: "introsSent", label: "Introductions you sent", value: num(network.sellSide.introsSent?.current) },
    { key: "introReplies", label: "Replies to your introductions", value: num(network.sellSide.introReplies?.current) },
  ];
};

export const officeAsSources = (office, sources) => {
  if (!office) return {};
  const bySource = {};
  if (sources.includes("network") && office.network) {
    const n = office.network;
    bySource.network = {
      days: office.days,
      buySide: { agentViews: n.agentViews, introsReceived: n.introsReceived },
      sellSide: { introsSent: n.introsSent, introReplies: n.introReplies },
    };
  }
  if (sources.includes("gitcha") && office.gitcha) {
    bySource.gitcha = { days: office.days, listingViews: office.gitcha.listingViews, contacts: office.gitcha.contacts };
  }
  return bySource;
};

const sameDays = (data, days) => data?.days == null || Number(data.days) === Number(days);

export const settleSnapshot = (previous, { sources, days, entries }) => {
  const ready = sources.length > 0 && sources.every((source) => {
    const entry = entries[source];
    return Boolean(entry?.data) && !entry.placeholder && sameDays(entry.data, days);
  });
  if (!ready) return previous;
  const key = sources.join(",");
  const bySource = Object.fromEntries(sources.map((source) => [source, entries[source].data]));
  const unchanged =
    previous &&
    previous.key === key &&
    Number(previous.days) === Number(days) &&
    sources.every((source) => previous.bySource[source] === bySource[source]);
  return unchanged ? previous : { key, days, bySource };
};

export const isSettledFor = (snapshot, sources, days) =>
  Boolean(snapshot) && snapshot.key === sources.join(",") && Number(snapshot.days) === Number(days);
